"use client";

import { cn } from "@/lib/tailwind";
import React, { Dispatch, HTMLAttributes, ReactNode, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

export type AccordionItem = {
  title: string | ReactNode;
  content: string | ReactNode;
  classNames?: Partial<{ container: string; trigger: string; content: string }>;
};

type AccordionProps = {
  items: AccordionItem[];
  autoCollapse?: boolean;
  customToggleRenderer?: React.ReactNode;
  className?: HTMLAttributes<HTMLUListElement>["className"];
  headerClasses?: HTMLAttributes<HTMLButtonElement>["className"];
  openIndices?: number[];
  reCalculateHeight?: boolean;
  expandIcon?: ReactNode;
  onToggleItem?: (index: number) => void;
  expandIconRotation?: string;
  setIsExpandAll?: Dispatch<SetStateAction<boolean>>;
};

const Accordion: React.FC<AccordionProps> = ({
  items,
  className,
  headerClasses,
  autoCollapse = false,
  customToggleRenderer,
  openIndices: controlledOpenIndices,
  reCalculateHeight = false,
  expandIcon,
  onToggleItem = () => {},
  expandIconRotation,
  setIsExpandAll
}) => {
  items = items?.filter(Boolean);
  //states
  const [openIndices, setOpenIndices] = useState<number[]>(controlledOpenIndices ?? []);
  const [conHeights, setConHeights] = useState<number[]>([]);

  // hooks
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  // methods
  const itemsCount = items?.length ?? 0;

  const toggleAccordion = useCallback(
    (index: number) => {
      setOpenIndices((prevIndices) => {
        let nextIndices: number[] = [];

        if (autoCollapse) {
          nextIndices = prevIndices.includes(index) ? [] : [index];
        } else if (prevIndices.includes(index)) {
          nextIndices = prevIndices.filter((i) => i !== index);
        } else {
          nextIndices = [...prevIndices, index];
        }

        if (setIsExpandAll) {
          const areAllExpanded = nextIndices.length === itemsCount && itemsCount > 0;
          setIsExpandAll(areAllExpanded);
        }

        return nextIndices;
      });

      onToggleItem(index);
    },
    [autoCollapse, itemsCount, onToggleItem, setIsExpandAll],
  );

  const handleSetAccordianHeight = useCallback(() => {
    if (contentRefs.current.length === 0) return;

    const res = [];

    for (const ele of contentRefs.current) {
      if (ele !== null) {
        res.push(ele.scrollHeight);
      }
    }

    setConHeights(res);
  }, []);

  useEffect(() => {
    handleSetAccordianHeight();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => {
        handleSetAccordianHeight();
      });
      contentRefs.current.forEach((ele) => {
        if (ele) {
          ro.observe(ele);
        }
      });
      return () => {
        ro.disconnect();
      };
    }
  }, [openIndices, reCalculateHeight, handleSetAccordianHeight]);

  useEffect(() => {
    if (Array.isArray(controlledOpenIndices)) {
      setOpenIndices(controlledOpenIndices);
    }
  }, [controlledOpenIndices]);

  return (
    <ul className={cn(className)}>
      {items?.map((item, index) => {
        const isOpen = openIndices.includes(index);

        return (
          <li
            key={index}
            className={cn("overflow-hidden border-t border-tertiary-3", item.classNames?.container)}
          >
            {/* Accordion Header */}
            <button
              {...(!customToggleRenderer && { onClick: () => toggleAccordion(index) })}
              className={cn(
                "flex w-full items-center justify-between py-4 focus:outline-none",
                isOpen ? "border-b border-tertiary-3" : "",
                headerClasses,
                item.classNames?.trigger,
              )}
            >
              <h3 className="text-sm font-normal">{item.title}</h3>
              {customToggleRenderer ? (
                <div onClick={() => toggleAccordion(index)}>{customToggleRenderer}</div>
              ) : (
                <div className="relative flex h-3 w-3 items-center justify-center flex-shrink-0">
                  {expandIcon ? (
                    <span
                      className={cn(
                        "transition-all",
                        expandIconRotation && !isOpen
                          ? "rotate-0"
                          : expandIconRotation && isOpen
                            ? expandIconRotation
                            : "-rotate-90",
                      )}
                    >
                      {expandIcon}
                    </span>
                  ) : (
                    <>
                      {/* Horizontal Line (static) */}
                      <div className="absolute h-0.5 w-3 bg-gray-800"></div>
                      {/* Vertical Line (rotates) */}
                      <div
                        className={`absolute h-3 w-0.5 transform bg-gray-800 transition-transform duration-300 ${
                          isOpen ? "rotate-90" : "rotate-0"
                        }`}
                      ></div>
                    </>
                  )}
                </div>
              )}
            </button>

            {/* Accordion Content */}
            <div
              ref={(el) => {
                contentRefs.current[index] = el;
              }}
              className={cn(
                "overflow-hidden transition-[max-height] duration-300 ease-in-out",
                item.classNames?.content,
              )}
              style={{
                maxHeight: isOpen ? (conHeights[index] ?? 0) : 0,
              }}
            >
              <div className="text-gray-700">{item.content}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default Accordion;
